import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { WorkItem } from '@robocorp/robocloud-search-iternal-sdk';
import { Activity } from '@robocorp/robocloud-internal-cds-sdk';
import { Box, Button, Menu, Tooltip, Typography, Table, TableRowProps } from '@robocorp/components';
import { IconLayoutRight, IconPaperclip } from '@robocorp/icons/iconic';
import { useNavigate } from 'react-router-dom';
import { CellButton, TextOverflow, WorkItemAvatar, Timestamp } from '~/components';
import { WorkItemState } from '~/types/resources';
import {
  WorkDataTableProcessRunCell,
  WorkDataTableStepRunCell,
  WorkItemIdCell,
} from '../../workItem/cells';

type RowData = WorkItem;

interface RowProps {
  canWriteWorkItemData: boolean;
  processSteps: Record<string, Activity>;
  onRetryWorkItem: (args: { processId: string; workItemIds: string[] }) => void;
  onDiscardWorkItem: (args: { processId: string; workItemIds: string[] }) => void;
  onDeleteWorkItem: (args: { processId: string; workItemIds: string[] }) => void;
  onDeleteWorkItemData: (args: { workItemId: string }) => void;
}

const WorkDataTableRow: FC<TableRowProps<RowData, RowProps>> = ({
  rowData: workItem,
  props: {
    processSteps,
    canWriteWorkItemData,
    onRetryWorkItem,
    onDiscardWorkItem,
    onDeleteWorkItem,
    onDeleteWorkItemData,
  },
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const onRowClick = () =>
    workItem?.id
      ? navigate(
          { pathname: `workItem/${workItem.id}`, search: window.location.search },
          { replace: true },
        )
      : null;

  return (
    <Table.Row onClick={onRowClick}>
      <WorkItemIdCell id={workItem.id} />
      <Table.Cell>
        <Tooltip
          text={
            <>
              {`${t('stateUpdated')} `}
              <Timestamp value={workItem?.stateTs} />
            </>
          }
        >
          <WorkItemAvatar state={workItem.state} />
        </Tooltip>
      </Table.Cell>
      <Table.Cell maxWidth={200}>
        <Tooltip text={workItem.failedWorkItemException?.message}>
          <TextOverflow maxWidth="100%">
            {workItem.failedWorkItemException?.message ?? '-'}
          </TextOverflow>
        </Tooltip>
      </Table.Cell>
      <Table.Cell maxWidth={160}>
        {workItem.failedWorkItemException?.type &&
          t(`workItemExceptionType.${workItem.failedWorkItemException?.type}`)}
      </Table.Cell>
      <Table.Cell maxWidth={160}>
        <Tooltip text={workItem.failedWorkItemException?.code}>
          <TextOverflow maxWidth="100%">
            {workItem.failedWorkItemException?.code ?? '-'}
          </TextOverflow>
        </Tooltip>
      </Table.Cell>
      <WorkDataTableProcessRunCell
        processName={workItem.processName}
        processId={workItem.processId}
        processRunId={workItem.processRunId}
        processRunNo={workItem.processRunNo}
      />
      <WorkDataTableStepRunCell
        step={processSteps[workItem.activityId]}
        processId={workItem.processId}
        processRunId={workItem.processRunId}
        processRunNo={workItem.processRunNo}
        activityId={workItem.activityId}
        activityRunId={workItem.activityRunId}
        queueTs={workItem.queueTs}
      />
      <Table.Cell maxWidth={300}>{JSON.stringify(workItem.payload) || t('noPayload')}</Table.Cell>
      <Table.Cell cellWrap="nowrap">
        <Box display="flex" alignItems="center">
          <IconPaperclip />
          <Typography ml={8}>{workItem.files?.length || 0}</Typography>
        </Box>
      </Table.Cell>
      <Table.Cell cellWrap="nowrap">
        <Timestamp value={workItem?.createTs} />
      </Table.Cell>
      <Table.Cell cellWrap="nowrap">
        <Timestamp value={workItem?.stateTs} />
      </Table.Cell>
      <Table.Cell controls>
        <Tooltip text={t('details')}>
          <Button
            icon={IconLayoutRight}
            variant="ghost"
            onClick={onRowClick}
            aria-label="Open side-bar"
          />
        </Tooltip>
      </Table.Cell>
      <Table.Cell align="center" controls>
        <Menu
          trigger={<CellButton loading={workItem.state === WorkItemState.InProgress} />}
          minWidth={200}
        >
          {workItem.state === WorkItemState.Failed && (
            <Menu.Item
              onClick={() =>
                onRetryWorkItem({ processId: workItem.processId, workItemIds: [workItem.id] })
              }
            >
              {t('retryWorkItem')}
            </Menu.Item>
          )}
          {workItem.state === WorkItemState.Failed && (
            <Menu.Item
              onClick={() =>
                onDiscardWorkItem({ processId: workItem.processId, workItemIds: [workItem.id] })
              }
            >
              {t('markAsComplete')}
            </Menu.Item>
          )}
          {canWriteWorkItemData &&
            workItem.state !== WorkItemState.InProgress &&
            !!workItem.payload && (
              <Menu.Item onClick={() => onDeleteWorkItemData({ workItemId: workItem.id })}>
                {t('workItem.deleteData')}
              </Menu.Item>
            )}
          <Menu.Item
            disabled={workItem.state === WorkItemState.InProgress}
            onClick={() =>
              onDeleteWorkItem({ processId: workItem.processId, workItemIds: [workItem.id] })
            }
          >
            {t('delete')}
          </Menu.Item>
        </Menu>
      </Table.Cell>
    </Table.Row>
  );
};

export default WorkDataTableRow;
